import React, {useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/common/Layout';
import SEO from '../components/common/SEO';
import Button from '../components/common/Button';
import { Link, useLocation } from 'react-router-dom';

const AppReleasePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const location = useLocation();
  
  useEffect(() => {
    // Check if the URL has the #waitlist hash
    if (location.hash === '#waitlist') {
      // Find the waitlist element and scroll to it
      const waitlistElement = document.getElementById('waitlist');
      if (waitlistElement) {
        waitlistElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);
  
  return (
    <Layout>
      <SEO 
        title={t('appRelease.pageTitle', 'Nemlingo Mobile App Coming Soon | Join the Waitlist')}
        description={t('appRelease.pageDescription', 'The Nemlingo mobile app is coming soon to Android and iOS. Join our waitlist to be the first to know when it launches and get exclusive early access.')}
        keywords={t('appRelease.pageKeywords', 'nemlingo app, language learning app, mobile app, android app, ios app, app release, language learning')}
      />
      
      <div className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
            {t('appRelease.title', 'Nemlingo Mobile App')} 
            <span className="block text-primary-600 mt-2">{t('appRelease.subtitle', 'Coming Soon')}</span>
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-xl text-gray-500">
            {t('appRelease.description', "We're working hard to bring the Nemlingo experience to your mobile devices. Join our waitlist to be the first to know when it launches.")}
          </p>
        </div>

        <div className="bg-primary-light rounded-lg p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            {t('appRelease.timelineTitle', 'Release Timeline')}
          </h2>
          
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-0 sm:left-1/2 h-full w-0.5 bg-primary-300 transform -translate-x-1/2"></div>
              
              {/* Timeline items */}
              <div className="relative z-10">
                {/* Item 1 */}
                <div className="mb-12 flex flex-col sm:flex-row items-center">
                  <div className="flex-1 sm:pr-8 sm:text-right mb-4 sm:mb-0">
                    <h3 className="text-xl font-bold text-primary">{t('appRelease.phase1Title', 'Beta Testing')}</h3>
                    <p className="text-gray-700">{t('appRelease.phase1Date', 'September 2025')}</p>
                    <p className="text-gray-600 mt-2">{t('appRelease.phase1Description', 'Limited beta release for early subscribers')}</p>
                  </div>
                  <div className="bg-primary rounded-full h-8 w-8 flex items-center justify-center shadow-lg border-4 border-white sm:mx-0 mx-auto">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div className="flex-1 sm:pl-8 hidden sm:block"></div>
                </div>
                
                {/* Item 2 */}
                <div className="mb-12 flex flex-col sm:flex-row items-center">
                  <div className="flex-1 sm:pr-8 hidden sm:block"></div>
                  <div className="bg-primary rounded-full h-8 w-8 flex items-center justify-center shadow-lg border-4 border-white sm:mx-0 mx-auto">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div className="flex-1 sm:pl-8 mb-4 sm:mb-0 sm:text-left text-center">
                    <h3 className="text-xl font-bold text-primary">{t('appRelease.phase2Title', 'Public Beta')}</h3>
                    <p className="text-gray-700">{t('appRelease.phase2Date', 'December 2025')}</p>
                    <p className="text-gray-600 mt-2">{t('appRelease.phase2Description', 'Open beta testing for all users')}</p>
                  </div>
                </div>
                
                {/* Item 3 */}
                <div className="mb-12 flex flex-col sm:flex-row items-center">
                  <div className="flex-1 sm:pr-8 sm:text-right mb-4 sm:mb-0">
                    <h3 className="text-xl font-bold text-primary">{t('appRelease.phase3Title', 'Official Launch')}</h3>
                    <p className="text-gray-700">{t('appRelease.phase3Date', 'February 2026')}</p>
                    <p className="text-gray-600 mt-2">{t('appRelease.phase3Description', 'Full release on App Store and Google Play')}</p>
                  </div>
                  <div className="bg-primary rounded-full h-8 w-8 flex items-center justify-center shadow-lg border-4 border-white sm:mx-0 mx-auto">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div className="flex-1 sm:pl-8 hidden sm:block"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('appRelease.featuresTitle', 'What to Expect')}
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-lg text-gray-700">{t('appRelease.feature1', 'Journal entries on the go')}</span>
              </li>
              <li className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-lg text-gray-700">{t('appRelease.feature2', 'Instant translation from your native language to the language you wish to learn.')}</span>
              </li>
              <li className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-lg text-gray-700">{t('appRelease.feature3', 'Voice recording for shadowing practice')}</span>
              </li>
              <li className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-lg text-gray-700">{t('appRelease.feature4', 'Push notifications for daily practice reminders')}</span>
              </li>
              <li className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-lg text-gray-700">{t('appRelease.feature5', 'Sync with your web account')}</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-100 rounded-lg p-8 shadow-inner" id='waitlist'>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('appRelease.waitlistTitle', 'Join the Waitlist')}
            </h3>
            <p className="text-gray-700 mb-6">
              {t('appRelease.waitlistDescription', 'Sign up to be notified when our app launches. Early subscribers will get exclusive access to beta features and premium content.')}
            </p>
            <div className="text-center">
              <Button
                onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLSd-pn7MaMQISX8x265Rwwe4cR2MtreDmuSa7lmORsMqJmUQCQ/viewform?usp=header', '_blank')}
                className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg transition duration-300"
              >
                {t('form.joinWaitlist', 'Join Waitlist')}
              </Button>
            </div>
          </div>
        </div>
        
        
        
        {/* <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {t('appRelease.questionsTitle', 'Have Questions?')}
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            {t('appRelease.questionsDescription', "If you have any questions about our upcoming mobile app or would like more information, please don't hesitate to contact us.")}
          </p>
          <Link to="/contact">
            <Button variant="outline" size="lg">
              {t('appRelease.contactUs', 'Contact Us')}
            </Button>
          </Link>
        </div> */}
      </div>
    </Layout>
  );
};

export default AppReleasePage;
